import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { GeolocationI, getGeolocation } from "../services/ipgeolocation";
import { getStripeSession, getStripeUrlV2, retrievePromotionCodeFromCouponId } from "../actions/stripeAction";
import { _roundTwoDecimals } from "../util/numbers";
import { COUNTRY_PRICING_MAP, DYNAMIC_ANNUAL_PRICE_IDS, FLOW_PRICE_MAP, FlowKeys } from "../util/static-price-data";
import { useRouteWatcher } from "../hooks/useRouteWatcher";

const FLOW_STORAGE_KEY = "checkoutFlow";
const FLOW_STORAGE_KEY_CUSTOM = "checkoutFlow-custom";
const FLOW_STORAGE_KEY_HOSTED = "checkoutFlow-hosted";
const FLOW_STORAGE_KEY_GIFT = "checkoutFlow-gift";

export const clearStripeSessionStorage = () => {
  sessionStorage.removeItem(FLOW_STORAGE_KEY_HOSTED);
  sessionStorage.removeItem(FLOW_STORAGE_KEY_CUSTOM);
  sessionStorage.removeItem(FLOW_STORAGE_KEY_GIFT);
};

const ROUTE_TO_FLOW_MAP: Record<string, FlowKeys> = {
  "/": "default",
  "/begin/": "default", // AppLovin traffic
  "/special-welcome/": "default", // learnwithimprint.com facebook traffic
  "/start/": "default", // learnonimprint.com google traffic
  "/welcome/": "default", // 404 fallback flow
  "/welcome-CTVLG/": "default", // CTV LG traffic
  "/welcome-CTVVIBE/": "default", // CTV VIBE traffic
  "/joinnow": "default", // goals onboarding test
  "/join/": "web_no_trial", // direct to pay flow (no trial)
  "/testroute/": "default", // test route flow TO BE REMOVED
  // "/get-started/": "web_paid_trial", // paid trial flow
  "/gift/start/": "gift_purchase", // gift purchase flow
  "/gift/redeem/": "gift_redeem", // gift redemption flow
};

type CountryT = keyof typeof COUNTRY_PRICING_MAP[keyof typeof COUNTRY_PRICING_MAP];
type CheckoutSessionT = {
  email: string;
  uid: string;
  metadata?: Record<string, string>;
};

type PaymentCheckoutSessionT = CheckoutSessionT & {
  mode: "payment";
  payment_intent_data?: { metadata: { [key: string]: string | number } };
};

type SubscriptionCheckoutSessionT = CheckoutSessionT & {
  mode: "subscription";
  subscription_data?: {
    metadata?: { [key: string]: string };
    description?: string;
  };
};

export type PriceDataT = {
  currency: string;
  annual: string;
  monthly: string;
  annualDiscounted: string;
  monthlyDiscounted: string;
  priceId: string;
  trial: boolean;
  percentOff: number;
  startDate?: Date;
  endDate?: Date;
};

interface PricingContextType {
  setFlow: (flow: FlowKeys) => void;
  priceData: PriceDataT;
  getStripeHostedSession: (checkoutSession: SubscriptionCheckoutSessionT | PaymentCheckoutSessionT) => Promise<string>;
  getStripeCustomSession: (checkoutSession: SubscriptionCheckoutSessionT | PaymentCheckoutSessionT) => Promise<string>;
  getStripeGiftRedeemSession: (code: string, checkoutSession: SubscriptionCheckoutSessionT | PaymentCheckoutSessionT) => Promise<string>;
  generateSuccessUrl: (uid: string) => string;
  userLocation: GeolocationI | null;
}

const PricingContext = createContext<PricingContextType>({
  setFlow: () => {},
  priceData: {
    currency: "$",
    annual: "124.99",
    monthly: "10.42",
    annualDiscounted: "99.99",
    monthlyDiscounted: "8.34",
    trial: true,
    priceId: DYNAMIC_ANNUAL_PRICE_IDS[124.99].trialDescription,
    percentOff: 20,
  },
  getStripeHostedSession: async () => "",
  getStripeCustomSession: async () => "",
  getStripeGiftRedeemSession: async () => "",
  generateSuccessUrl: () => "",
  userLocation: null,
});

/**
 * This context is used to provide the pricing data to the app
 * and streamline the checkout process.
 * @returns {Object}
 * @property {function} setFlow - Set the current flow. The available flows are defined in the FLOW_PRICE_MAP.
 * @property {PriceDataT} priceData - The price data for the current flow. This should be used to display the price data to the user.
 * @property {function} getStripeHostedSession - Get the stripe hosted session.
 * @property {function} getStripeCustomSession - Get the stripe custom session.
 * @property {function} getStripeGiftRedeemSession - Get the stripe gift redeem session.
 * @property {function} generateSuccessUrl - Get the paddle success URL.
 */
export const PricingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentFlow, setCurrentFlow] = useState<FlowKeys>("default");
  const [priceData, setPriceData] = useState<PriceDataT>({
    currency: "$",
    annual: "124.99",
    monthly: "10.42",
    annualDiscounted: "99.99",
    monthlyDiscounted: "8.34",
    trial: true,
    priceId: DYNAMIC_ANNUAL_PRICE_IDS[124.99].trialDescription,
    percentOff: 20,
  });
  const [userLocation, setUserLocation] = useState<GeolocationI | null>(null);

  // initialize the flow and user location
  useEffect(() => {
    const fetchUserLocation = async () => {
      const location = await getGeolocation();
      if (location) {
        setUserLocation(location);
      }
    };

    fetchUserLocation();
    const flow = sessionStorage.getItem("checkoutFlow") as FlowKeys;
    if (flow) {
      setCurrentFlow(flow);
    }
  }, []);

  // update the price data based on the user location
  useEffect(() => {
    if (userLocation) {
      console.log("DEBUG userLocation", userLocation);
      const { cost, coupon, priceId, trial, endDate, startDate } = FLOW_PRICE_MAP[currentFlow];
      const data = COUNTRY_PRICING_MAP[cost as keyof typeof COUNTRY_PRICING_MAP];
      const country = userLocation.is_eu ? "EU" : userLocation.country_code2;
      const localizedData = Object.keys(data).includes(country) ? data[country as CountryT] : data.US;
      const localizedDataWithDiscounts = {
        ...localizedData,
        annualDiscounted: coupon ? _roundTwoDecimals(Number(localizedData.annual) * (1 - (coupon.amount / 100))).toFixed(2) : localizedData.annual,
        monthlyDiscounted: coupon ? _roundTwoDecimals(Number(localizedData.monthly) * (1 - (coupon.amount / 100))).toFixed(2) : localizedData.monthly,
        priceId,
        trial,
        currency: localizedData.symbol,
        percentOff: coupon?.amount ?? 0,
        startDate,
        endDate,
      };

      setPriceData(localizedDataWithDiscounts);
    }
  }, [userLocation, currentFlow]);

  // build the base checkout session data
  const _buildBaseCheckoutSession = (checkoutSession: SubscriptionCheckoutSessionT | PaymentCheckoutSessionT) => {
    const { email, uid, metadata, mode } = checkoutSession;

    const { priceId, coupon, trial } = FLOW_PRICE_MAP[currentFlow];

    const isPaymentSession = mode === "payment";
    const subscriptionData = isPaymentSession || (!trial && !checkoutSession.subscription_data) ? undefined : {
      ...(trial && { trial_period_days: 7 }),
      ...(checkoutSession.subscription_data && { ...checkoutSession.subscription_data }),
    };

    return {
      email,
      uid,
      mode,
      price_ids: [{ price: priceId, quantity: 1 }],
      currency: userLocation?.currency?.code ?? "USD",
      ...(coupon && { discounts: [{ coupon: coupon.id }] }),
      ...(subscriptionData && { subscription_data: subscriptionData }),
      ...(metadata && { metadata }),
      ...(isPaymentSession && checkoutSession.payment_intent_data && { payment_intent_data: checkoutSession.payment_intent_data }),
    };
  };

  // get the stripe hosted session
  const getStripeHostedSession = async (checkoutSession: SubscriptionCheckoutSessionT | PaymentCheckoutSessionT) => {
    const storageSessionClientId = sessionStorage.getItem(FLOW_STORAGE_KEY_HOSTED);
    if (storageSessionClientId) {
      return storageSessionClientId;
    }

    const { email, uid } = checkoutSession;

    if (!email || !uid) {
      throw new Error("missing email or uid");
    }

    const checkoutData = _buildBaseCheckoutSession(checkoutSession);
    const { cancelUrl, successUrl, cost, trial } = FLOW_PRICE_MAP[currentFlow];
    const { search } = window.location;

    // this is used for analytics on direct to pay flows
    const priceParam = trial ? "" : `&price=${cost}`;

    const url = await getStripeUrlV2({
      ...checkoutData,
      cancel_url: `${cancelUrl}${search}`,
      success_url: `${successUrl}?session_id={CHECKOUT_SESSION_ID}&uid=${uid}${priceParam}`,
    });

    sessionStorage.setItem(FLOW_STORAGE_KEY_HOSTED, url);

    return url;
  };

  // get the stripe custom session
  const getStripeCustomSession = async (checkoutSession: SubscriptionCheckoutSessionT | PaymentCheckoutSessionT) => {
    const { email, uid } = checkoutSession;
    const storageSessionClientId = sessionStorage.getItem(FLOW_STORAGE_KEY_CUSTOM);
    if (storageSessionClientId) {
      return storageSessionClientId;
    }

    if (!email || !uid) {
      throw new Error("missing email or uid");
    }

    const checkoutData = _buildBaseCheckoutSession(checkoutSession);
    const { successUrl, cost, trial } = FLOW_PRICE_MAP[currentFlow];

    const priceParam = trial ? "" : `&price=${cost}`;

    const sessionClientId = await getStripeSession({
      ...checkoutData,
      return_url: `${successUrl}?session_id={CHECKOUT_SESSION_ID}&uid=${uid}${priceParam}`,
    });

    sessionStorage.setItem(FLOW_STORAGE_KEY_CUSTOM, sessionClientId);

    return sessionClientId;
  };

  const getStripeGiftRedeemSession = async (code: string, checkoutSession: SubscriptionCheckoutSessionT | PaymentCheckoutSessionT) => {
    const { email, uid } = checkoutSession;
    const storageSessionClientId = sessionStorage.getItem(FLOW_STORAGE_KEY_GIFT);
    if (storageSessionClientId) {
      return storageSessionClientId;
    }

    if (!email || !uid) {
      throw new Error("missing email or uid");
    }

    const promotionCode = await retrievePromotionCodeFromCouponId(code);

    const checkoutData = _buildBaseCheckoutSession(checkoutSession);
    const { successUrl } = FLOW_PRICE_MAP[currentFlow];
    const { search } = window.location;

    const sessionClientId = await getStripeUrlV2({
      ...checkoutData,
      discounts: [{ promotion_code: promotionCode?.id }],
      cancel_url: `${successUrl}${search}`,
      success_url: `${successUrl}?session_id={CHECKOUT_SESSION_ID}&uid=${uid}`,
    });

    sessionStorage.setItem(FLOW_STORAGE_KEY_GIFT, sessionClientId);

    return sessionClientId;
  };

  const generateSuccessUrl = (uid: string) => {
    const { successUrl, cost, trial } = FLOW_PRICE_MAP[currentFlow];

    const priceParam = trial ? "" : `&price=${cost}`;
    return `${successUrl}?session_id={CHECKOUT_SESSION_ID}&uid=${uid}${priceParam}`;
  };

  // set the flow and clear the session storage
  const setFlow = (flow: FlowKeys) => {
    sessionStorage.setItem(FLOW_STORAGE_KEY, flow);
    sessionStorage.removeItem(FLOW_STORAGE_KEY_HOSTED);
    sessionStorage.removeItem(FLOW_STORAGE_KEY_CUSTOM);
    setCurrentFlow(flow);
  };

  // Handle special discount on landing pages
  const handleRouteChange = useCallback((location: Location) => {
    console.log("DEBUG route change", location);
    const pathname = location.pathname.endsWith("/") ? location.pathname : `${location.pathname}/`;
    const flow = ROUTE_TO_FLOW_MAP[pathname];
    if (flow) {
      setFlow(flow);
    }
  }, []);

  useRouteWatcher(handleRouteChange);

  return (
    <PricingContext.Provider
      value={{
        setFlow,
        priceData,
        getStripeHostedSession,
        getStripeCustomSession,
        getStripeGiftRedeemSession,
        generateSuccessUrl,
        userLocation,
      }}
    >
      {children}
    </PricingContext.Provider>
  );
};

export const usePricingContext = () => {
  const context = useContext(PricingContext);
  if (context === undefined) {
    console.error("usePricing must be used within a PricingProvider");
  }

  return context;
};
